import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
    WithStyles
} from "@material-ui/core";
import { styles } from "./SubscriptionDateRangeCopyDialog.styles";
import * as UserDetailsAction from "../../../stores/user/UserDetailsAction";
import UserDetailsModel from "../../../models/user/UserDetailsModel";
import CloseIcon from "@material-ui/icons/Close";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { AutocompleteWithCheckboxes } from "../../components/FormComponents/AutocompleteWithCheckboxes";

export interface ISubscriptionDateRangeCopyDialogProps extends WithStyles<typeof styles> {
    title: string;
    text: string;
    subscriptionName?: string;
    startDate?: number;
    endDate?: number;
    userName?: string;
    onClose: () => void;
    onCopy: (selectedUsers: string[]) => void;
}

const SubscriptionDateRangeCopyDialogPure: React.FunctionComponent<ISubscriptionDateRangeCopyDialogProps> = (
    props: ISubscriptionDateRangeCopyDialogProps
) => {
    const { title, text, subscriptionName, startDate, endDate, userName, onClose, onCopy, classes } = props;
    const dispatch = useDispatch();
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    useEffect(() => {
        dispatch(UserDetailsAction.requestAllUsersDetails());
    }, [dispatch]);

    const allUsersFromState = useSelector(state => state.userDetails?.all);

    let allUsers;
    if (allUsersFromState) {
        allUsers = Object.values(allUsersFromState)?.map((user: UserDetailsModel) => ({
            id: user.id,
            name: user.email
        }));
    }

    const isCopyButtonDisabled = selectedUsers?.length === 0;

    return (
        <>
            <Dialog open fullWidth scroll="paper" onClose={() => onClose()} maxWidth="md">
                <DialogTitle>
                    <Typography variant="subtitle1" gutterBottom>
                        {title}
                    </Typography>
                    <div className={classes.text}>
                        <Typography variant="caption" gutterBottom>
                            {text}
                        </Typography>
                    </div>
                    <Typography variant="h6">{userName && `Source user: ${userName}`}</Typography>
                    <Typography variant="h6">{subscriptionName && `Subscription name: ${subscriptionName}`}</Typography>
                    <Typography variant="h6">{startDate && `Start date: ${startDate}`}</Typography>
                    <Typography variant="h6">{endDate && `End date: ${endDate}`}</Typography>
                    <IconButton
                        aria-label="close"
                        style={{ position: "absolute", right: "1px", top: "1px" }}
                        onClick={() => onClose()}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <div>
                        <AutocompleteWithCheckboxes
                            id="all-users"
                            allItems={allUsers || []}
                            selectedItems={selectedUsers}
                            onChange={newValue => setSelectedUsers(newValue)}
                        ></AutocompleteWithCheckboxes>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Tooltip title={isCopyButtonDisabled ? "Please select at least one user" : ""}>
                        <span>
                            <BlueButton onClick={() => onCopy(selectedUsers)} disabled={isCopyButtonDisabled}>
                                Copy
                            </BlueButton>
                        </span>
                    </Tooltip>
                    <BlueButton onClick={() => onClose()}>Cancel</BlueButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const SubscriptionDateRangeCopyDialog = withStyles(styles)(SubscriptionDateRangeCopyDialogPure);
